<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        lg="6"
      >
        <naj-box
          v-if="showNationalAddress"
          :title="local.nationalAddress.title"
          :no-bottom-margin="true"
          :no-h-gutters="true"
        >
          <v-row>
            <v-col
              v-if="(caseParty.nationalAddresses || []).length"
              cols="12"
              lg="6"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="local.nationalAddress.nationalAddresses"
                :value.sync="caseParty.nationalAddress.id"
                :items="distinctNationalAddressesWithText"
                :clearable="false"
                validates="required"
                item-value="id"
                item-text="text"
                @change="addressChanged"
              ></naj-input>
            </v-col>
            <v-col
              cols="12"
              lg="6"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="local.nationalAddress.city"
                :value.sync="caseParty.nationalAddress.cityId"
                :value-name.sync="caseParty.nationalAddress.city"
                :items="cities"
                item-text="name"
                item-value="id"
                :disabled="!cities.length || nationalAddressDisabled"
                validates="required"
                @change="cityChanged"
              ></naj-input>
            </v-col>
            <v-col
              v-if="caseParty.nationalAddress.cityId && !(caseParty.nationalAddresses || []).length"
              cols="12"
              lg="6"
            >
              <naj-input
                :type-id="formFieldType.selectbox"
                :label="local.nationalAddress.district"
                :value.sync="caseParty.nationalAddress.districtId"
                :value-name.sync="caseParty.nationalAddress.district"
                :items="districts[caseParty.nationalAddress.cityId]"
                item-text="name"
                item-value="id"
                :disabled="!(districts[caseParty.nationalAddress.cityId] || []).length || disabled"
              ></naj-input>
            </v-col>
          </v-row>
        </naj-box>

        <naj-address
          v-if="showResidenceAddress"
          :title="local.titleHeader.residenceAddress"
          :form-model="caseParty.residenceAddress"
          :required="true"
        >
        </naj-address>
      </v-col>

      <v-col
        cols="12"
        lg="6"
      >
        <contact-form
          :form-model="caseParty.contact"
          :required="true"
          :no-bottom-margin="true"
          :no-h-gutters="true"
          :cols="6"
        ></contact-form>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" lg="12" v-if="partyRolesList && partyRolesList.length > 0">
        <naj-box
            :title="local.partyTypeSelection.reference"
            :collapsed="false"
            :no-bottom-margin="true"
            :no-h-gutters="true"
        >
          <party-role
            :form-model="caseParty"
            :options="partyRolesList"
            :existing-parties="Object.values(prosecutors)"
            :label="local.partyTypeSelection.reference"
            :required="true"
            :cols="6"
          />
        </naj-box>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ContactForm from '../party-types/forms/contact-form';
import { createNamespacedHelpers } from 'vuex';
import { socialIdentityTypeEnum , partyTypeEnum, casePartyRoleEnum} from '@/helpers/enum';
import PartyRole from '../sub-party-role-selector/party-role.vue';
import usePartyAttributesMixin from '@/modules/lawsuit/mixins/usePartyAttributesMixin';

const { mapState: mapLUTState, mapActions: mapLUTActions } = createNamespacedHelpers('LookupsModule');
const { mapGetters: mapLawsuitGetters } = createNamespacedHelpers('LawsuitModule');
export default {
  inject: ['$validator'],
  components: { ContactForm, PartyRole },
  mixins: [usePartyAttributesMixin],
  props: {
    caseParty: {
      type: Object,
      default: () => { },
      required: true,
    },
  },
  computed: {
    ...mapLUTState(['cities', 'districts']),
    ...mapLawsuitGetters(['prosecutors', 'selectedCaseType']),
    showNationalAddress() {
      return this.caseParty.socialIdType == socialIdentityTypeEnum.nationalId || this.caseParty.socialIdType == socialIdentityTypeEnum.regularResidency;
    },
    showResidenceAddress() {
      return this.caseParty.socialIdType != socialIdentityTypeEnum.nationalId && this.caseParty.socialIdType != socialIdentityTypeEnum.regularResidency;
    },
    nationalAddressDisabled() {
      return this.caseParty.nationalAddress.isRetrieved && this.caseParty.nationalAddress.cityId;
    },
    distinctNationalAddressesWithText() {
      const addresses = (this.caseParty.nationalAddresses || []);
      return [...new Map(addresses.map(address => [address.text, address])).values()];
    },
    caseTypeId() {
      return this.selectedCaseType?.id;
    },
    legalPositionId() {
      return this.caseParty?.casePartyRole ?? casePartyRoleEnum.prosecutor;
    },
    partyTypeId() {
      return this.caseParty?.partyType ?? partyTypeEnum.person;
    },
    caseParties() {
      return this.prosecutors || [];
    },
  },
  watch: {
    'caseParty.nationalAddress.cityNameEnglish'() {
      this.setNationalAddressCityId();
    },
  },
  mounted() {
    this.getCities().then(() => {
      this.setNationalAddressCityId();
    }).finally(() => {
      this.caseParty.nationalAddress.cityId && this.cityChanged();
    });
  },
  methods: {
    ...mapLUTActions(['getCities', 'getDistrictsByCity']),
    cityChanged() {
      this.getDistrictsByCity(this.caseParty.nationalAddress.cityId);
    },
    addressChanged(index) {
      var selectedAddress = this.caseParty.nationalAddresses[index] || {};
      this.setNationalAddressCityId()
        .then(() => { this.$set(this.caseParty.nationalAddress, 'isRetrieved', !!this.caseParty.nationalAddress.cityId || !!this.caseParty.nationalAddress.city); });
      this.$set(this.caseParty.nationalAddress, 'district', selectedAddress.district);
      this.$set(this.caseParty.nationalAddress, 'districtId', selectedAddress.districtId);
      this.$set(this.caseParty.nationalAddress, 'street', selectedAddress.street);
      this.$set(this.caseParty.nationalAddress, 'postalCode', selectedAddress.postCode);
      this.$set(this.caseParty.nationalAddress, 'poBox', selectedAddress.additionalNumber);
      this.$set(this.caseParty.nationalAddress, 'buildingNumber', selectedAddress.buildingNumber);
      this.$set(this.caseParty.nationalAddress, 'regionName', selectedAddress.regionName);
      this.$set(this.caseParty.nationalAddress, 'cityNameEnglish', selectedAddress.cityNameEnglish);
      this.$set(this.caseParty.nationalAddress, 'flatNumber', null);
    },
    setNationalAddressCityId() {
      return new Promise((resolve, reject) => {
        const city = this.caseParty.nationalAddress.cityNameEnglish && (this.cities || []).find(c => (c.nationalAddressCityEnglishName || '').split(',').some(n => n === this.caseParty.nationalAddress.cityNameEnglish)) || {};
        this.$set(this.caseParty.nationalAddress, 'cityId', (city || {}).id ? city.id : null);
        this.$set(this.caseParty.nationalAddress, 'city', (city || {}).id ? city.name : null);
        if (this.caseParty.nationalAddress.cityId) this.getDistrictsByCity(this.caseParty.nationalAddress.cityId);
        if (city) resolve(city.id); else reject(null);
      });
    },
  },
};
</script>